import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Dropdown,
  Popconfirm,
  Popover,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
  Flex,
} from "antd";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import {
  capitalizeFirstLetter,
  formatCurrency,
  formatDate,
  getCustomFieldsItems,
  idToObjectMap,
  insertIf,
} from "common/utils";
import currency from "currency.js";
import { useFetchCourtsOfLawQuery } from "features/courtsOfLawTable/courtsOfLawAPI";
import { useFetchCreditorSummariesQuery } from "features/creditors/agencyPortal/creditorsAPI";
import {
  collectorsWithRolesSelector,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { collectorFullName } from "features/home/utils";
import { useGetLawFirmsQuery } from "features/lawFirmsTable/lawFirmsAPI";
import AddJudgmentModal from "features/legal/components/addJudgmentModal";
import AddLawsuitModal from "features/legal/components/addLawsuitModal";
import AddLegalFeesModal from "features/legal/components/addLegalFeesModal";
import LegalLettersModal from "features/legal/components/legalLettersModal";
import LawsuitAuditLogsModal from "features/legal/components/lawsuitAuditLogsModal";
import { useDeleteLawsuitMutation, useFetchLawsuitsQuery } from "features/legal/legalAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useNavigate } from "react-router-dom";
import { useFetchAllLegalCustomFieldsQuery } from "features/userDefinedFields/legalCustomFieldsAPI";
import { useFetchProcessServersQuery } from "features/processServersTable/processServersAPI";
import { UserDefinedFieldValues } from "components/userDefinedFieldValues";
import { useAppearance } from "providers/appearanceProvider";
import { TriggerModal } from "components/triggerModal";
import { TableAppearanceModal } from "features/appearance";
import { reconcileColumns } from "features/appearance/components/tableAppearanceModal";

const StyledInfoValue = styled.div`
  margin-left: 4px;
`;

const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  font-size: 24px;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledTag = styled(Tag)`
  margin: 4px 4px;
`;

const StyledTable = styled(Table)`
  cursor: pointer;
  width: 100%;

  & .ant-table-cell:not(th) {
    padding: 4px 8px !important;
  }
`;

const TABLE_ID = "legalInfo";

/**
 * Determines which accounts to use for a lawsuit by combining non-archived accounts.
 *
 * @param {Object} lawsuit - The lawsuit object containing status and account information
 * @param {Array} lawsuit.accounts - Original accounts associated with the lawsuit
 * @param {Array} lawsuit.judgmentAccounts - Judgment accounts (created after judgment is applied)
 *
 * @returns {Array} Returns all non-archived original accounts combined with all judgment accounts.
 *
 * @description
 * This function implements the business rule that lawsuits should display:
 * - Original accounts that are NOT legally archived (still in litigation or partial judgment)
 * - All judgment accounts
 *
 * Excludes archived accounts (in_litigation_archived, judgment_applied_archived)
 * to avoid double-counting when judgment has been applied.
 *
 * NOTE: This logic matches backend's Lawsuit.legal_balance property.
 * For total balance display, prefer using lawsuit.legalBalance directly.
 */
const getAccountsForLawsuit = (lawsuit) => {
  // Filter out archived original accounts
  const nonArchivedOriginalAccounts = (lawsuit.accounts || []).filter(
    (account) =>
      account.legalStatus !== "in_litigation_archived" &&
      account.legalStatus !== "judgment_applied_archived",
  );

  // Combine non-archived original accounts with all judgment accounts
  return [...nonArchivedOriginalAccounts, ...(lawsuit.judgmentAccounts || [])];
};

export default function LegalInfo({ debtorId }) {
  const [currentLawsuit, setCurrentLawsuit] = useState(null);
  const [isLegalWizardView, setIsLegalWizardView] = useState(null);
  const [isJudgmentWizardView, setIsJudgmentWizardView] = useState(null);
  const [isLegalFeeView, setIsLegalFeeView] = useState(null);
  const [isLegalLettersView, setLegalLettersView] = useState(null);
  const [isLawsuitAuditLogsView, setIsLawsuitAuditLogsView] = useState(null);
  const { data: lawFirms } = useGetLawFirmsQuery();
  const { data: courtsOfLaw } = useFetchCourtsOfLawQuery();
  const { data: processServers } = useFetchProcessServersQuery();
  const { data: lawsuits, isLoading } = useFetchLawsuitsQuery({ debtorId });
  const { data: creditors } = useFetchCreditorSummariesQuery();
  const [postDeleteLawsuit] = useDeleteLawsuitMutation();
  const { data: legalCustomFields } = useFetchAllLegalCustomFieldsQuery();
  const { appearance, isLoading: isAppearanceLoading } = useAppearance();

  const { collectorsWithRoles: collectors } = useFetchCollectorsQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      collectorsWithRoles: collectorsWithRolesSelector(result),
    }),
  });
  const {
    data: {
      [PERMISSIONS.LAWSUIT__GET]: isGetLawsuitAuthorized,
      [PERMISSIONS.LAWSUIT__CREATE]: isCreateLawsuitAuthorized,
      [PERMISSIONS.LAWSUIT__UPDATE]: isUpdateLawsuitAuthorized,
      [PERMISSIONS.LAWSUIT__DELETE]: isDeleteLawsuitAuthorized,
      [PERMISSIONS.LAWSUIT__APPLY_JUDGEMENT]: isLawsuitApplyJudgmentAuthorized,
      [PERMISSIONS.LEGAL__DOWNLOAD_DOCUMENTS]: isLegalDownloadDocuments,
    },
  } = useAuthorizations();
  const navigate = useNavigate();

  const creditorIdToCreditorMap = useMemo(() => {
    if (!creditors) {
      return {};
    }
    return idToObjectMap(creditors);
  }, [creditors]);

  const courtsOfLawMap = useMemo(() => {
    if (!courtsOfLaw) {
      return {};
    }
    return idToObjectMap(courtsOfLaw);
  }, [courtsOfLaw]);

  const lawFirmsMap = useMemo(() => {
    if (!lawFirms) {
      return {};
    }
    return idToObjectMap(lawFirms);
  }, [lawFirms]);

  const processServersMap = useMemo(() => {
    if (!processServers) {
      return {};
    }
    return idToObjectMap(processServers);
  }, [processServers]);

  const addLegal = () => {
    setIsLegalWizardView(true);
    setCurrentLawsuit(null);
  };

  const editLawsuit = (lawsuit) => {
    setIsLegalWizardView(true);
    setCurrentLawsuit(lawsuit);
  };

  const applyJudgment = (lawsuit) => {
    setIsJudgmentWizardView(true);
    setCurrentLawsuit(lawsuit);
  };

  const addLegalFees = (lawsuit) => {
    setIsLegalFeeView(true);
    setCurrentLawsuit(lawsuit);
  };

  const onCancelJudgmentWizard = () => {
    setIsJudgmentWizardView(false);
  };

  const onDoneJudgmentWizard = () => {
    setIsJudgmentWizardView(false);
  };

  const onCancelLegalWizard = () => {
    setIsLegalWizardView(false);
  };

  const onCancelLegalFees = () => {
    setIsLegalFeeView(false);
  };

  const onDoneLegalWizard = () => {
    setIsLegalWizardView(false);
  };

  const sendLetters = (lawsuit) => {
    setLegalLettersView(lawsuit);
  };
  const onDoneLegalLetters = () => {
    setLegalLettersView(false);
  };
  const onCancelLegalLetters = () => {
    setLegalLettersView(false);
  };

  const viewLawsuitAuditLogs = (lawsuit) => {
    setIsLawsuitAuditLogsView(lawsuit);
    setCurrentLawsuit(lawsuit);
  };
  const onCancelLawsuitAuditLogs = () => {
    setIsLawsuitAuditLogsView(false);
  };

  const items = [
    {
      label: "Manage Lawsuit Logs",
      key: "lawsuit_audit_logs",
      hidden: false,
    },
    {
      label: "Apply Judgment",
      key: "apply_judgment",
      // actionHandler: applyJudgment,
      hidden: !isLawsuitApplyJudgmentAuthorized,
    },
    {
      label: "Create Legal Documents",
      key: "create_documents",
      // actionHandler: sendLetters,
      hidden: !isLegalDownloadDocuments,
    },
    {
      label: "Add Legal Fees",
      key: "add_legal_fees",
      // actionHandler: addLegalFees,
      hidden: false,
    },
  ]
    .filter((item) => !item.hidden)
    // insert dividers between items
    .flatMap((value, index, array) =>
      index < array.length - 1
        ? [
            value,
            {
              type: "divider",
            },
          ]
        : value,
    );

  const handleMenuClick = ({ key, domEvent }, record) => {
    // This prevents navigation to invoice detail when clicking on a dropdown item
    domEvent.preventDefault();
    domEvent.stopPropagation();

    // Call the action handler defined in the items dictionary
    const action = {
      lawsuit_audit_logs: viewLawsuitAuditLogs,
      apply_judgment: applyJudgment,
      create_documents: sendLetters,
      add_legal_fees: addLegalFees,
    }[key];
    action(record);
  };

  const columns = [
    {
      key: "id",
      title: "ID",
      dataIndex: "id",
      visible: true,
      sorter: (a, b) => a.id - b.id,
    },
    {
      key: "legalCaseNumber",
      title: "Legal Case #",
      dataIndex: "legalCaseNumber",
      render: (text) => text || "-",
      visible: true,
      sorter: (a, b) => (a.legalCaseNumber || "").localeCompare(b.legalCaseNumber || ""),
    },
    {
      key: "accountIds",
      title: "Account IDs",
      render: (text, record) => {
        const accounts = getAccountsForLawsuit(record);
        return (
          accounts
            ?.map((account) => {
              return `${account.externalId}`;
            })
            .join(", ") ?? "-"
        );
      },
      visible: true,
    },
    {
      key: "debtorIds",
      title: "Debtor IDs",
      width: 200,
      render: (text, record) =>
        record.debtors
          ?.map((debtor) => {
            return `${debtor.externalId} (${debtor.name ? `${debtor.name}` : ""})`;
          })
          .join(", ") ?? "-",
      visible: true,
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (text, record) => {
        if (record.legalStatus === "judgment") {
          return <Tag color="green">JUDGMENT APPLIED</Tag>;
        }
        if (record.status === "judgment_applied") {
          return <Tag color="green">JUDGMENT APPLIED</Tag>;
        }
        if (record.status === "in_litigation") {
          return <Tag color="orange">IN LITIGATION</Tag>;
        }
        if (record.status === "partial_judgment") {
          return <Tag color="orange">PARTIAL JUDGMENT</Tag>;
        }
        return "-";
      },
      visible: true,
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
    },
    {
      key: "courtId",
      title: "Court Name (Code)",
      dataIndex: "courtId",
      render: (text) => {
        const lawsuitCourt = courtsOfLawMap[text];
        if (lawsuitCourt?.code) {
          return `${lawsuitCourt?.name ? `${lawsuitCourt.name} ` : ""}(${lawsuitCourt?.code})`;
        }
        return "-";
      },
      visible: true,
      sorter: (a, b) => {
        const courtA = courtsOfLawMap[a.courtId];
        const courtB = courtsOfLawMap[b.courtId];
        const nameA = courtA?.name || "";
        const nameB = courtB?.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      key: "lawFirmId",
      title: "Law Firm/Attorney",
      dataIndex: "lawFirmId",
      render: (text, record) => lawFirmsMap[text]?.name ?? "-",
      visible: true,
      sorter: (a, b) => {
        const firmA = lawFirmsMap[a.lawFirmId];
        const firmB = lawFirmsMap[b.lawFirmId];
        const nameA = firmA?.name || "";
        const nameB = firmB?.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      key: "processServerId",
      title: "Process Server",
      dataIndex: "processServerId",
      render: (text, record) => processServersMap[text]?.name ?? "-",
      visible: true,
      sorter: (a, b) => {
        const serverA = processServersMap[a.processServerId];
        const serverB = processServersMap[b.processServerId];
        const nameA = serverA?.name || "";
        const nameB = serverB?.name || "";
        return nameA.localeCompare(nameB);
      },
    },
    {
      key: "customFieldValues",
      title: "User Defined Fields",
      dataIndex: "customFieldValues",
      render: (text, record) => {
        const customFieldItems = getCustomFieldsItems(record.customFieldValues, legalCustomFields);

        // @ts-ignore
        return <UserDefinedFieldValues customFieldItems={customFieldItems} />;
      },
      visible: true,
    },
    {
      title: "Total Balance",
      dataIndex: "totalBalance",
      key: "totalBalance",
      visible: true,
      sorter: (a, b) => currency(a.legalBalance).value - currency(b.legalBalance).value,
      render: (text, record) => {
        const accounts = getAccountsForLawsuit(record);

        const totalPrincipal = accounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.principalBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalInterest = accounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.interestBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalClientInterest = accounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.clientInterestBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalPrejudgmentInterest = accounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.prejudgmentInterestBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalFees = accounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.totalFeesBalance),
          currency(0, { precision: 4 }),
        ).value;

        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(totalPrincipal),
          },
          {
            key: "interest",
            label: "Interest",
            value: formatCurrency(totalInterest),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(totalClientInterest),
          },
          {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(totalPrejudgmentInterest),
          },
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(totalFees),
          },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.legalBalance)}</strong>,
          },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <Row align="middle">
            <StyledInfoValue>{formatCurrency(record.legalBalance)}</StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Total Balance Breakdown"
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </Row>
        );
      },
    },
    {
      title: "Total Debtor Balance",
      dataIndex: "totalDebtorLawsuitBalance",
      key: "totalDebtorLawsuitBalance",
      visible: true,
      sorter: (a, b) => {
        const accountsA = getAccountsForLawsuit(a);
        const accountsB = getAccountsForLawsuit(b);
        const filteredAccountsA = accountsA.filter(
          (account) => !account.debtorIds?.includes(debtorId),
        );
        const filteredAccountsB = accountsB.filter(
          (account) => !account.debtorIds?.includes(debtorId),
        );
        const totalA = filteredAccountsA.reduce(
          (acc, account) => acc.add(account.paymentCategories.totalBalance),
          currency(0, { precision: 4 }),
        ).value;
        const totalB = filteredAccountsB.reduce(
          (acc, account) => acc.add(account.paymentCategories.totalBalance),
          currency(0, { precision: 4 }),
        ).value;
        return totalA - totalB;
      },
      render: (text, record) => {
        const accounts = getAccountsForLawsuit(record);

        // filter out accounts that belongs to this debtor
        const filteredAccounts = accounts.filter(
          (account) => !account.debtorIds?.includes(debtorId),
        );

        const totalBalance = filteredAccounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.totalBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalPrincipal = filteredAccounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.principalBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalInterest = filteredAccounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.interestBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalClientInterest = filteredAccounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.clientInterestBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalPrejudgmentInterest = filteredAccounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.prejudgmentInterestBalance),
          currency(0, { precision: 4 }),
        ).value;

        const totalFees = filteredAccounts.reduce(
          (acc, account) => acc.add(account.paymentCategories.totalFeesBalance),
          currency(0, { precision: 4 }),
        ).value;

        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(totalPrincipal),
          },
          {
            key: "interest",
            label: "Agency Interest",
            value: formatCurrency(totalInterest),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(totalClientInterest),
          },
          {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(totalPrejudgmentInterest),
          },
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(totalFees),
          },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(totalBalance)}</strong>,
          },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <Row align="middle">
            <StyledInfoValue>{formatCurrency(totalBalance)}</StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Total Balance Breakdown"
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </Row>
        );
      },
    },
    {
      title: "Placement Date",
      dataIndex: "placementDate",
      key: "placementDate",
      visible: true,
      sorter: (a, b) => (a.placementDate || "").localeCompare(b.placementDate || ""),
      render: (text, record) => formatDate(record.placementDate) ?? "-",
    },
    {
      title: "Acknowledgement Date",
      dataIndex: "acknowledgementDate",
      key: "acknowledgementDate",
      visible: true,
      sorter: (a, b) => (a.acknowledgementDate || "").localeCompare(b.acknowledgementDate || ""),
      render: (text, record) => formatDate(record.acknowledgementDate) ?? "-",
    },
    {
      title: "Closed Date",
      dataIndex: "closeDate",
      key: "closeDate",
      visible: true,
      sorter: (a, b) => (a.closeDate || "").localeCompare(b.closeDate || ""),
      render: (text, record) => formatDate(record.closeDate) ?? "-",
    },
    {
      title: "Judge Name",
      dataIndex: "judgeName",
      key: "judgeName",
      visible: true,
      sorter: (a, b) => (a.judgeName || "").localeCompare(b.judgeName || ""),
      render: (text) => text || "-",
    },
    {
      title: "Agency Approval Date",
      dataIndex: "agencyApprovalDate",
      key: "agencyApprovalDate",
      visible: true,
      sorter: (a, b) => (a.agencyApprovalDate || "").localeCompare(b.agencyApprovalDate || ""),
      render: (text, record) => formatDate(record.agencyApprovalDate) ?? "-",
    },
    {
      title: "Filing Date",
      dataIndex: "filingDate",
      key: "filingDate",
      visible: true,
      sorter: (a, b) => (a.filingDate || "").localeCompare(b.filingDate || ""),
      render: (text, record) => formatDate(record.filingDate) ?? "-",
    },
    {
      title: "Process Date",
      dataIndex: "processDate",
      key: "processDate",
      visible: true,
      sorter: (a, b) => (a.processDate || "").localeCompare(b.processDate || ""),
      render: (text, record) => formatDate(record.processDate) ?? "-",
    },
    {
      title: "Answered Date",
      dataIndex: "answeredDate",
      key: "answeredDate",
      visible: true,
      sorter: (a, b) => (a.answeredDate || "").localeCompare(b.answeredDate || ""),
      render: (text, record) => formatDate(record.answeredDate) ?? "-",
    },
    {
      title: "Next Court Date",
      dataIndex: "nextCourtDate",
      key: "nextCourtDate",
      visible: true,
      sorter: (a, b) => (a.nextCourtDate || "").localeCompare(b.nextCourtDate || ""),
      render: (text, record) => formatDate(record.nextCourtDate) ?? "-",
    },
    {
      title: "Dismissal Date",
      dataIndex: "dismissalDate",
      key: "dismissalDate",
      visible: true,
      sorter: (a, b) => (a.dismissalDate || "").localeCompare(b.dismissalDate || ""),
      render: (text, record) => formatDate(record.dismissalDate) ?? "-",
    },
    {
      title: "Actions",
      fixed: "right",
      className: "invoice-actions",
      width: 50,
      key: "actions",
      visible: true,
      render: (_, record) => {
        return (
          <Row wrap={false} align="middle">
            <Space size="middle">
              {isUpdateLawsuitAuthorized && (
                <Tooltip placement="bottom" title="Edit" key="edit">
                  <EditOutlined key="edit" onClick={() => editLawsuit(record)} />
                </Tooltip>
              )}
              {isDeleteLawsuitAuthorized && (
                <Popconfirm
                  placement="topLeft"
                  okText="Yes"
                  title="Are you sure you want to delete this lawsuit?"
                  onConfirm={async () => {
                    const result = await postDeleteLawsuit({
                      debtorId,
                      lawsuitId: record.id,
                    });
                    if ("data" in result) {
                      message.success("Lawsuit successfully removed");
                    } else {
                      message.error("Failed to remove lawsuit");
                    }
                  }}
                >
                  <DeleteOutlined
                    disabled={lawsuits?.find((lawsuit) => lawsuit.id === record?.id)?.entryDate}
                    key="delete"
                  />
                </Popconfirm>
              )}
            </Space>
            {(isLawsuitApplyJudgmentAuthorized || isLegalDownloadDocuments) && (
              <Dropdown
                menu={{
                  // @ts-ignore
                  items,
                  onClick: (clickHandlerProp) => handleMenuClick(clickHandlerProp, record),
                }}
                trigger={["click"]}
              >
                <Button type="text">
                  <StyledEllipsisOutlined />
                </Button>
              </Dropdown>
            )}
          </Row>
        );
      },
    },
  ];

  const reconciledColumns = reconcileColumns(columns, appearance?.items?.[TABLE_ID]).filter(
    (column) => column.visible,
  );

  const expandableColumns = [
    {
      title: "Account ID",
      dataIndex: "id",
      key: "externalId",
      render: (text, record) => record.externalId ?? "-",
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (text, record) => (
        <Col>
          {record?.isClosed ? <StyledTag color="red">CLOSED</StyledTag> : null}
          {record?.legalStatus === "judgment" ? (
            <StyledTag color="green">JUDGMENT APPLIED</StyledTag>
          ) : null}
          {record?.legalStatus === "judgment_applied_archived" ? (
            <StyledTag color="green">JUDGMENT ARCHIVED</StyledTag>
          ) : null}
          {record?.legalStatus === "in_litigation" ? (
            <StyledTag color="orange">IN LITIGATION</StyledTag>
          ) : null}
          {record?.legalStatus === "judgment_applied" ? (
            <StyledTag color="green">JUDGMENT APPLIED</StyledTag>
          ) : null}
        </Col>
      ),
    },
    {
      title: "Judgment Date",
      dataIndex: "judgmentDate",
      key: "judgmentDate",
      render: (text, record) => formatDate(record.judgmentDate) ?? "-",
    },
    {
      title: "Current Balance",
      dataIndex: "balance",
      key: "balance",
      render: (text, record) => {
        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(record.paymentCategories.principalBalance),
          },
          {
            key: "interest",
            label: "Interest",
            value: formatCurrency(record.paymentCategories.interestBalance),
          },
          {
            key: "clientInterest",
            label: "Client Interest",
            value: formatCurrency(record.paymentCategories.clientInterestBalance),
          },
          ...insertIf(record.legalStatus === "judgment", {
            key: "prejudgmentInterest",
            label: "Prejudgment Interest",
            value: formatCurrency(record.paymentCategories.prejudgmentInterestBalance),
          }),
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(record.paymentCategories.totalFeesBalance),
          },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.paymentCategories.totalBalance)}</strong>,
          },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <Row align="middle">
            <StyledInfoValue>
              {formatCurrency(record.paymentCategories.totalBalance)}
            </StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Total Balance Breakdown"
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </Row>
        );
      },
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (text, record) => {
        const creditor = creditorIdToCreditorMap[record.creditorId];
        if (creditor?.code) {
          return `${creditor?.name} (${creditor?.code})`;
        }
        return creditor?.name ?? "-";
      },
    },
    {
      title: "Current Creditor",
      dataIndex: "currentCreditor",
      key: "currentCreditor",
      render: (text, record) => record.currentCreditorName ?? "-",
    },
    {
      title: "Co-Debtors",
      dataIndex: "coDebtors",
      key: "coDebtors",
      render: (text, record) => {
        const codebtorIds = record.debtorIds?.filter((id) => id.toString() !== debtorId) ?? [];

        if (codebtorIds.length === 0) {
          return "-";
        }

        return (
          <Col>
            {codebtorIds.map((codebtorId) => (
              <div key={codebtorId}>
                <Button
                  type="link"
                  onClick={() => {
                    navigate(`/debtors/${codebtorId}`);
                  }}
                >
                  {/* NOTE: We don't pull these codebtor names, because it'd be too expensive of a query on the backend. */}
                  {codebtorId}
                </Button>
              </div>
            ))}
          </Col>
        );
      },
    },
    {
      title: "Collector",
      dataIndex: "collector",
      key: "collector",
      render: (text, record) =>
        capitalizeFirstLetter(
          collectorFullName(collectors?.find((x) => x.id === record.assigneeId)),
        ),
    },
  ];

  const ExpandedRow = useCallback(
    (record) => {
      const accounts = getAccountsForLawsuit(record);

      return (
        <>
          <h4>Related Accounts</h4>
          <StyledTable
            bordered
            scroll={{ x: "max-content" }}
            // @ts-ignore
            columns={expandableColumns}
            dataSource={accounts}
            pagination={false}
            loading={isLoading}
          />
        </>
      );
    },
    // we don't want columns to be a dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading, lawsuits],
  );

  return isGetLawsuitAuthorized ? (
    <>
      <Col>
        <Flex justify="space-between" align="middle">
          {isCreateLawsuitAuthorized && (
            <Button onClick={addLegal} icon={<PlusOutlined />} type="link">
              Add Lawsuit
            </Button>
          )}
          <TriggerModal
            modal={TableAppearanceModal}
            tableId={TABLE_ID}
            defaultColumns={columns}
            paginationSelector={false}
          >
            <Tooltip title="Customize Appearance">
              <Button shape="circle" icon={<SettingOutlined />} type="text" />
            </Tooltip>
          </TriggerModal>
        </Flex>
        <StyledTable
          bordered
          scroll={{ x: "max-content" }}
          // @ts-ignore
          columns={reconciledColumns}
          dataSource={lawsuits}
          expandable={{
            expandedRowRender: ExpandedRow,
          }}
          loading={isLoading}
        />
      </Col>
      {isLegalWizardView && (
        <AddLawsuitModal
          lawsuit={currentLawsuit}
          open={isLegalWizardView}
          onCancel={onCancelLegalWizard}
          onOk={onDoneLegalWizard}
        />
      )}
      {isJudgmentWizardView && (
        <AddJudgmentModal
          lawsuit={currentLawsuit}
          onOk={onDoneJudgmentWizard}
          onCancel={onCancelJudgmentWizard}
        />
      )}
      {isLegalFeeView && (
        <AddLegalFeesModal
          lawsuit={currentLawsuit}
          onCancel={onCancelLegalFees}
          title="Add Legal Fees"
        />
      )}
      <LegalLettersModal
        title="Legal Letters"
        open={isLegalLettersView}
        onOk={onDoneLegalLetters}
        onCancel={onCancelLegalLetters}
      />
      {isLawsuitAuditLogsView && (
        <LawsuitAuditLogsModal
          title="Lawsuit Logs"
          open={isLawsuitAuditLogsView}
          lawsuit={currentLawsuit}
          onCancel={onCancelLawsuitAuditLogs}
        />
      )}
    </>
  ) : null;
}
